export interface RoadEnvelope {
  x: number;
  z: number;
  yaw: number;
  width: number;
  length: number;
}
/** Body-sized downstream storage beyond the complete authored junction turn. */
export function junctionExitPose(
  body: RoadEnvelope,
  route: readonly { x: number; z: number; node?: number; junction?: number }[],
  node: { id: number; x: number; z: number },
  clearance: number,
): RoadEnvelope | undefined {
  let index = route.findIndex((p) => (p.junction ?? p.node) === node.id);
  if (index < 0) return;
  while (index + 1 < route.length && (route[index + 1].junction ?? route[index + 1].node) === node.id)
    index++;
  for (let i = index; i < route.length - 1; i++) {
    const a = route[i],
      b = route[i + 1],
      dx = b.x - a.x,
      dz = b.z - a.z,
      length = Math.hypot(dx, dz);
    if (length < 0.01) continue;
    const x = a.x - node.x,
      z = a.z - node.z,
      dot = (x * dx + z * dz) / length;
    const distance = Math.max(
      0,
      -dot + Math.sqrt(Math.max(0, dot * dot - (x * x + z * z - clearance * clearance))),
    );
    if (distance > length) continue;
    return {
      ...body,
      x: a.x + (dx * distance) / length,
      z: a.z + (dz * distance) / length,
      yaw: Math.atan2(-dx, -dz),
    };
  }
}
/** Four-axis separating-plane test for actual oriented road-body footprints. */
export function overlapsRoadBody(a: RoadEnvelope, b: RoadEnvelope, margin = 0.08): boolean {
  return roadBodyPenetration(a, b, margin) > 0;
}
/** Predict the occupied body along the actual turn, rather than extending a
 * tangent rectangle through a safe pavement corner. Movement still performs
 * its own exact footprint check on every step. */
export function routeBodyOverlaps(
  body: RoadEnvelope,
  route: readonly { x: number; z: number }[],
  obstacle: RoadEnvelope,
  distance: number,
): boolean {
  const pose = { ...body };
  if (overlapsRoadBody(pose, obstacle, 0.12)) return true;
  let remaining = distance;
  for (const next of route) {
    const dx = next.x - pose.x,
      dz = next.z - pose.z,
      length = Math.hypot(dx, dz);
    if (length < 0.001) continue;
    const travel = Math.min(length, remaining),
      samples = Math.ceil(travel / 0.4),
      yaw = Math.atan2(-dx, -dz),
      turn = Math.atan2(Math.sin(yaw - pose.yaw), Math.cos(yaw - pose.yaw)),
      from = { ...pose };
    for (let i = 1; i <= samples; i++) {
      const along = (travel * i) / samples;
      pose.x = from.x + (dx * along) / length;
      pose.z = from.z + (dz * along) / length;
      pose.yaw = from.yaw + turn * Math.min(1, along / 1.5);
      if (overlapsRoadBody(pose, obstacle, 0.18)) return true;
    }
    remaining -= travel;
    if (remaining <= 0.001) break;
  }
  return false;
}
/** Signed minimum overlap. This also lets an externally blocked body rotate out. */
export function roadBodyPenetration(a: RoadEnvelope, b: RoadEnvelope, margin = 0.08): number {
  const ax = Math.cos(a.yaw),
    az = -Math.sin(a.yaw),
    afX = -Math.sin(a.yaw),
    afZ = -Math.cos(a.yaw);
  const bx = Math.cos(b.yaw),
    bz = -Math.sin(b.yaw),
    bfX = -Math.sin(b.yaw),
    bfZ = -Math.cos(b.yaw);
  const dx = b.x - a.x,
    dz = b.z - a.z;
  const aligned = Math.abs(ax * bx + az * bz),
    crossed = Math.abs(ax * bfX + az * bfZ);
  const aw = a.width * 0.5,
    al = a.length * 0.5,
    bw = b.width * 0.5,
    bl = b.length * 0.5;
  // The same four separating axes, without temporary axis arrays/projections.
  return (
    Math.min(
      aw + bw * aligned + bl * crossed - Math.abs(dx * ax + dz * az),
      al + bw * crossed + bl * aligned - Math.abs(dx * afX + dz * afZ),
      bw + aw * aligned + al * crossed - Math.abs(dx * bx + dz * bz),
      bl + aw * crossed + al * aligned - Math.abs(dx * bfX + dz * bfZ),
    ) + margin
  );
}

/** A shallow rear-corner overlap can shrink along the road while the narrowest
 * lateral overlap is unchanged. Require no deeper overlap on any separating
 * axis and genuine separation on at least one, including both turn headings. */
export function separatesRoadBody(
  before: RoadEnvelope,
  after: RoadEnvelope,
  obstacle: RoadEnvelope,
  margin = 0.08,
): boolean {
  const basis = (body: RoadEnvelope) => ({ cos: Math.cos(body.yaw), sin: Math.sin(body.yaw) });
  const a = basis(before),
    b = basis(after),
    o = basis(obstacle);
  const projection = (body: RoadEnvelope, basis: { cos: number; sin: number }, x: number, z: number) =>
    (Math.abs(x * basis.cos - z * basis.sin) * body.width) / 2 +
    (Math.abs(-x * basis.sin - z * basis.cos) * body.length) / 2;
  let separating = false;
  for (const axes of [a, b, o])
    for (const [x, z] of [
      [axes.cos, -axes.sin],
      [-axes.sin, -axes.cos],
    ]) {
      const other = projection(obstacle, o, x, z) + margin;
      const prior =
        projection(before, a, x, z) +
        other -
        Math.abs((before.x - obstacle.x) * x + (before.z - obstacle.z) * z);
      const next =
        projection(after, b, x, z) +
        other -
        Math.abs((after.x - obstacle.x) * x + (after.z - obstacle.z) * z);
      if (prior <= 0 || next > prior + 0.00001) return false;
      if (next < prior - 0.000001) separating = true;
    }
  return separating;
}
